// src/components/VideoLibrary.js
import React, { useState, useEffect } from 'react';
import { Video, X } from 'lucide-react';
import sanityClient from '../lib/sanityClient';

function VideoLibrary() {
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    // Fetch videos from Sanity
    sanityClient
      .fetch(
        `*[_type == "trainingVideo"] {
          _id,
          title,
          description,
          type,
          url,
          "thumbnailUrl": thumbnail.asset->url,
          category,
          featured,
          "date": publishedAt
        }`
      )
      .then((data) => {
        setVideos(data);
        setLoading(false);
      })
      .catch((err) => {
        console.error('Error fetching videos:', err);
        if (err.message && err.message.includes('CORS')) {
          setError('CORS Error: Check Sanity CORS settings and authentication');
        } else if (err.statusCode === 403) {
          setError('Authentication Error: Check your Sanity token');
        } else {
          setError('Failed to load videos. Please try again later.');
        }
        setLoading(false);
      });
  }, []);

  // Handle video selection
  const handleVideoSelect = (video) => {
    setSelectedVideo(video);
    setIsModalOpen(true);
    // Add overflow hidden to body to prevent scrolling when modal is open
    document.body.style.overflow = 'hidden';
  };

  // Handle modal close
  const handleCloseModal = () => {
    setIsModalOpen(false);
    // Restore body overflow when modal is closed
    document.body.style.overflow = 'auto';
  };

  // YouTube thumbnail extraction function
  const getYouTubeInfo = (url) => {
    if (!url) return { id: null, thumbnailUrl: null };

    // Regular expressions to match YouTube URL patterns
    const regexps = [
      /(?:https?:\/\/)?(?:www\.)?youtube\.com\/watch\?v=([a-zA-Z0-9_-]+)(?:&.*)?/,
      /(?:https?:\/\/)?(?:www\.)?youtu\.be\/([a-zA-Z0-9_-]+)(?:\?.*)?/,
      /(?:https?:\/\/)?(?:www\.)?youtube\.com\/embed\/([a-zA-Z0-9_-]+)(?:\?.*)?/,
    ];

    let videoId = null;

    // Try each regex pattern to extract video ID
    for (const regex of regexps) {
      const match = url.match(regex);
      if (match && match[1]) {
        videoId = match[1];
        break;
      }
    }

    // Return video ID and thumbnail URL if found
    if (videoId) {
      return {
        id: videoId,
        thumbnailUrl: `https://img.youtube.com/vi/${videoId}/hqdefault.jpg`,
      };
    }

    return { id: null, thumbnailUrl: null };
  };

  // Display loading state
  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="bg-white p-6 rounded-lg shadow-md">
          <p className="text-lg">Loading videos...</p>
        </div>
      </div>
    );
  }

  // Display error state
  if (error) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="bg-white p-6 rounded-lg shadow-md text-red-600">
          <p className="text-lg">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-6">
      {/* Page Title */}
      <div className="mb-8 flex items-center justify-between">
        <div>
          <h2 className="text-2xl md:text-3xl font-bold text-gray-800">Training Videos</h2>
          <p className="text-gray-600">
            Improve your skills with our collection of training videos
          </p>
        </div>
      </div>

      {/* Video Grid */}
      {videos.length > 0 ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {videos.map((video) => {
            // Get YouTube thumbnail if it's a YouTube video
            const { thumbnailUrl: ytThumbnail } =
              video.type === 'youtube' ? getYouTubeInfo(video.url) : { thumbnailUrl: null };

            return (
              <div
                key={video._id}
                className="bg-white rounded-lg shadow-md overflow-hidden cursor-pointer transform hover:scale-105 transition-transform duration-200"
                onClick={() => handleVideoSelect(video)}
              >
                <div className="relative">
                  <img
                    src={video.thumbnailUrl || ytThumbnail || '/placeholder-thumbnail.jpg'}
                    alt={video.title}
                    className="w-full h-48 object-cover"
                    onError={(e) => {
                      // Fallback if Sanity thumbnail or YouTube thumbnail fails
                      e.target.src = '/placeholder-thumbnail.jpg';
                    }}
                  />
                  <div className="absolute inset-0 bg-black bg-opacity-40 flex items-center justify-center opacity-0 hover:opacity-100 transition-opacity duration-200">
                    <div className="bg-yellow-500 rounded-full p-3">
                      <Video size={24} className="text-gray-900" />
                    </div>
                  </div>
                  {video.featured && (
                    <div className="absolute top-2 right-2 bg-yellow-500 text-gray-900 text-xs font-bold px-2 py-1 rounded">
                      FEATURED
                    </div>
                  )}
                  <div className="absolute bottom-2 right-2 bg-gray-900 text-white text-xs px-2 py-1 rounded">
                    {video.type === 'youtube' ? 'YouTube' : 'MP4'}
                  </div>
                </div>
                <div className="p-4">
                  <h3 className="font-bold text-gray-800 mb-1 line-clamp-1">{video.title}</h3>
                  <p className="text-gray-600 text-sm line-clamp-2">{video.description}</p>
                  <div className="flex items-center mt-3">
                    <span className="bg-gray-100 text-gray-800 text-xs font-medium px-2.5 py-0.5 rounded">
                      {video.category}
                    </span>
                    <span className="text-gray-500 text-xs ml-auto">{video.date}</span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="text-center py-8">
          <p className="text-gray-600">No videos found. Add some videos in the Sanity Studio!</p>
        </div>
      )}

      {/* Video Modal */}
      {isModalOpen && selectedVideo && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-lg w-full max-w-5xl">
            {/* Modal Header */}
            <div className="flex justify-between items-center p-4 border-b">
              <h3 className="text-xl font-bold text-gray-800">{selectedVideo.title}</h3>
              <button
                onClick={handleCloseModal}
                className="text-gray-400 hover:text-gray-800 focus:outline-none"
              >
                <X size={24} />
              </button>
            </div>

            {/* Video Player */}
            <div className="aspect-w-16 aspect-h-9 relative">
              <div className="w-full h-64 md:h-96 bg-gray-800">
                {selectedVideo.type === 'youtube' ? (
                  <iframe
                    width="100%"
                    height="100%"
                    src={selectedVideo.url.replace('watch?v=', 'embed/')}
                    title={selectedVideo.title}
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  ></iframe>
                ) : (
                  <video
                    width="100%"
                    height="100%"
                    controls
                    autoPlay
                    className="w-full h-full"
                    poster={
                      selectedVideo.thumbnailUrl ||
                      getYouTubeInfo(selectedVideo.url).thumbnailUrl ||
                      '/placeholder-thumbnail.jpg'
                    }
                  >
                    <source src={selectedVideo.url} type="video/mp4" />
                    Your browser does not support the video tag.
                  </video>
                )}
              </div>
            </div>

            {/* Video Info */}
            <div className="p-4">
              <p className="text-gray-600 mt-1">{selectedVideo.description}</p>
              <div className="flex items-center mt-3">
                <span className="bg-yellow-100 text-yellow-800 text-xs font-medium px-2.5 py-0.5 rounded">
                  {selectedVideo.category}
                </span>
                {selectedVideo.featured && (
                  <span className="bg-blue-100 text-blue-800 text-xs font-medium ml-2 px-2.5 py-0.5 rounded">
                    Featured
                  </span>
                )}
                <span className="text-gray-500 text-sm ml-auto">{selectedVideo.date}</span>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default VideoLibrary;
